/**
 * PATH: packages/ui/src/components/global-search/HubsBar.tsx
 * FILE_ID: UI-GLOBALSEARCH-HUBSBAR-001
 * PURPOSE: Always-visible "Hubs" launcher pinned to the bottom of the
 *          GlobalSearch palette (below results, above the keyboard hints).
 * WHY: Product decision (2026-06-13) — cross-hub links were removed from the
 *      sidebar rail and relocated into search so the palette is the single
 *      entry point for hub navigation. Click behaviour mirrors the old
 *      sidebar hub list (SSO redirect / in-shell tab via `onHubClick`), so
 *      navigation is unchanged — only its location moved.
 * FLOW:
 * ┌──────────────────┐   ┌───────────────────┐   ┌───────────────────────┐
 * │ hubs[] +         │ → │ row of pill        │ → │ onHubClick(id, href)  │
 * │ onHubClick       │   │ buttons            │   │ | window.location     │
 * └──────────────────┘   └───────────────────┘   └───────────────────────┘
 * DEPENDENCIES:
 * - ../types ExternalLink: shared hub link shape (id/label/href/icon/status)
 * RELATED:
 * - GlobalSearch.tsx: mounts this bar via `pinnedHubs`/`onPinnedHubClick`
 * - sidebar/index.tsx: the rail Hubs section this replaces (hideExternalLinks)
 */

import { cn } from '../../lib/utils'
import type { ExternalLink } from '../types'

type HubLink = ExternalLink & { status?: 'production' | 'development' | 'planned' }

interface HubsBarProps {
  hubs: HubLink[]
  onHubClick?: (hubId: string, href: string) => void
  onClose: () => void
  currentHubId?: string
}

/**
 * Resolve a single hub click to its effect. Pure routing decision so the
 * onClick stays trivial: planned hubs are inert; otherwise prefer the
 * host's `onHubClick` (SSO / in-shell tab) and fall back to a hard nav.
 */
function activateHub(
  hub: HubLink,
  onHubClick: ((hubId: string, href: string) => void) | undefined,
  onClose: () => void,
): void {
  if (hub.status === 'planned') return
  if (onHubClick && hub.id) {
    onHubClick(hub.id, hub.href)
    onClose()
    return
  }
  if (hub.href) window.location.href = hub.href
  onClose()
}

export function HubsBar({ hubs, onHubClick, onClose, currentHubId }: HubsBarProps) {
  if (!hubs || hubs.length === 0) return null

  return (
    <div className="border-t border-border bg-muted/50 px-4 py-2.5">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Hubs
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
        {hubs.map((hub) => {
          const Icon = hub.icon
          const isActive = !!currentHubId && hub.id === currentHubId
          const isPlanned = hub.status === 'planned'
          return (
            <button
              key={hub.id || hub.href}
              type="button"
              disabled={isPlanned}
              onClick={() => activateHub(hub, onHubClick, onClose)}
              title={hub.description || hub.label}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                isActive
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted',
                isPlanned && 'cursor-not-allowed opacity-50',
              )}
            >
              {Icon ? <Icon className="h-3.5 w-3.5 flex-shrink-0" /> : null}
              <span>{hub.label}</span>
              {hub.status === 'development' && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                  DEV
                </span>
              )}
              {hub.status === 'planned' && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[8px] font-semibold text-muted-foreground">
                  SOON
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default HubsBar
